import { Stack, Typography, Box, CircularProgress } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ExtractParams } from '@/types/params';

import NoRowsOverlay from '@/@mui/data-grid/NoRowsOverlay';
import { useGetPayments } from '@/api/endpoints/payment/useGetPayments';
import { useGetStripePaymentHistory } from '@/api/endpoints/payment/useGetStripePaymentHistory';
import { useGetStripeSubscriptions } from '@/api/endpoints/payment/useGetStripeSubscriptions';
import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { formatFullDate } from '@/utils/date';

import RentSubscriptionCard from '@/components/resident/RentSubscriptionCard';

// Utility function to format currency values
const formatCurrency = (amount: number, isInCents = false): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })
    .format(amount / (isInCents ? 100 : 1))
    .replace('$', '');

const TenantViewPaymentListSubpage: React.FC = () => {
  const { id } = useParams<ExtractParams<PMPRoutes.tenantViewPaymentList>>();
  const { session } = useAuth();

  // Get payments from Stripe including pending transactions
  const { data: stripePayments = [], isLoading: isStripePaymentsLoading } =
    useGetStripePaymentHistory(
      {
        tenant_id: id,
      },
      !!session?.access_token
    );

  // Fallback to database payments if Stripe API fails
  const { data: dbPayments = [], isLoading: isDbPaymentsLoading } = useGetPayments(
    {
      tenant_id: id,
    },
    !!session?.access_token && stripePayments.length === 0 && !isStripePaymentsLoading
  );

  // Get Stripe subscriptions for this tenant (only pass tenant_id if id is defined and non-empty)
  const stripeSubscriptionsParams = id ? { tenant_id: id } : undefined;
  const { data: stripeSubscriptions = [], isLoading: isSubscriptionsLoading } =
    useGetStripeSubscriptions(stripeSubscriptionsParams, !!session?.access_token);

  // Determine which payments source to use
  const payments = stripePayments.length > 0 ? stripePayments : dbPayments;

  // Find the active subscription if it exists
  const activeSubscription = useMemo(() => {
    // First check in stripe subscriptions as they are more reliable/up-to-date
    const stripeSubscription = stripeSubscriptions.find(sub => sub.status === 'active');
    if (stripeSubscription) return stripeSubscription;

    // Fallback to the payment records for backward compatibility
    return payments.find(
      payment =>
        payment.status === 'active' && payment.type === 'rent' && payment.isSubscription === true
    );
  }, [payments, stripeSubscriptions]);

  // Determine if this is a Stripe subscription
  const isStripeSubscription = useMemo(() => {
    if (!activeSubscription) return false;
    return stripeSubscriptions.some(sub => sub.id === activeSubscription.id);
  }, [activeSubscription, stripeSubscriptions]);

  const isLoading = isStripePaymentsLoading || isDbPaymentsLoading || isSubscriptionsLoading;

  const handleSubscriptionCancelled = useCallback(() => {
    // No-op: Data will refresh due to react-query cache invalidation
  }, []);

  // Helper function to get color based on status
  const getStatusColor = (status: string): string => {
    if (status === 'completed' || status === 'active') {
      return 'success.main';
    }
    if (status === 'pending') {
      return 'warning.main';
    }
    return 'error.main';
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'period',
        headerName: 'Rent Period',
        width: 150,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params: GridRenderCellParams) => (
          <Typography
            variant="body2"
            sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'amount',
        headerName: 'Amount',
        width: 120,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params: GridRenderCellParams) => (
          <Typography
            variant="body2"
            sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
          >
            ${formatCurrency(params.value)}
          </Typography>
        ),
      },
      {
        field: 'date',
        headerName: 'Payment Date',
        width: 180,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params: GridRenderCellParams) => (
          <Typography
            variant="body2"
            sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
          >
            {formatFullDate(new Date(params.value))}
          </Typography>
        ),
      },
      {
        field: 'status',
        headerName: 'Rent Status',
        width: 120,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params: GridRenderCellParams) => (
          <Typography
            variant="body2"
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              textTransform: 'capitalize',
              color: getStatusColor(params.value as string),
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        minWidth: 200,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params: GridRenderCellParams) => (
          <Typography
            variant="body2"
            sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
          >
            {params.value}
          </Typography>
        ),
      },
    ],
    []
  );

  const PaymentNoRowsOverlay = useCallback(() => <NoRowsOverlay entityName="Payment" />, []);

  if (isLoading && !payments.length && !stripeSubscriptions.length) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack direction="column" spacing={2}>
      <Typography variant="dataGridBody">View this tenant's rent payment history.</Typography>
      {activeSubscription && (
        <RentSubscriptionCard
          activeSubscription={activeSubscription}
          onCancelled={handleSubscriptionCancelled}
          isStripeSubscription={isStripeSubscription}
          tenantId={id}
          isPropertyManager
        />
      )}
      <Box sx={{ width: '100%', minHeight: 200 }}>
        <DataGrid
          rows={payments}
          columns={columns}
          density="comfortable"
          disableColumnFilter
          disableColumnMenu
          disableEval
          loading={isLoading}
          getRowHeight={() => 'auto'}
          slots={{
            noRowsOverlay: PaymentNoRowsOverlay,
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'date', sort: 'desc' }],
            },
          }}
          disableRowSelectionOnClick
          hideFooter
          sx={{ height: 'auto', minHeight: 200 }}
        />
      </Box>
    </Stack>
  );
};

export default TenantViewPaymentListSubpage;
